/**
 * The report panel: a fixed card anchored above the bubble, not a page.
 *
 * Deliberately not a Modal — no backdrop and no focus trap, so the page being
 * reported stays readable while the reporter writes about it, and so the panel
 * is trivial to leave out of the next capture.
 *
 * Where the viewport is too small to be written beside, the card becomes the
 * screen instead: see `support-panel-layout` for which viewports those are.
 */
import CloseIcon from "@mui/icons-material/Close";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import SupportComposer from "./support-composer";
import type { ComposerCaret } from "./support-composer-editor";
import type { DraftPicture } from "./support-draft-pictures";
import { fullScreenPanelQuery } from "./support-panel-layout";
import { useSupportScrollLock } from "./use-support-scroll-lock";

/** One slot in the picture strip — thumbnails and the two add tiles alike. */
interface SupportReportPanelProps {
	/** Once a conversation exists it replaces the composer, in place. */
	conversation: React.ReactNode;
	/** One box, not a title plus a body: the fewer fields, the more reports. */
	message: string;
	pictures: DraftPicture[];
	/** Read when a picture is added, to put its [picture N] line at the cursor. */
	inputRef: React.Ref<ComposerCaret>;
	isSending: boolean;
	isCapturing: boolean;
	error: string | null;
	onMessageChange: (value: string) => void;
	/** Opens the picture large, where it can be marked up. */
	onEditPicture: (key: number) => void;
	onRemovePicture: (key: number) => void;
	onAddPicture: () => void;
	/** Null where the browser cannot capture another surface at all. */
	onAddOtherSurface: (() => void) | null;
	/** Opens the list of conversations the reporter already has. */
	/** The tab strip, rendered above whatever the selected tab shows. */
	tabs: React.ReactNode;
	onUploadPicture: (file: File) => void;
	onSend: () => void;
	onClose: () => void;
}

const SupportReportPanel: React.FC<SupportReportPanelProps> = ({
	conversation,
	message,
	pictures,
	inputRef,
	isSending,
	isCapturing,
	error,
	onMessageChange,
	onEditPicture,
	onRemovePicture,
	onAddPicture,
	onAddOtherSurface,
	tabs,
	onUploadPicture,
	onSend,
	onClose,
}) => {
	const theme = useTheme();
	const fullScreen = fullScreenPanelQuery(theme);
	const isFullScreen = useMediaQuery(fullScreen);
	// The page behind must not move while the panel is the screen: a drag on
	// the tab strip or the composer has nowhere of its own to go.
	useSupportScrollLock(isFullScreen);

	return (
		<Paper
			elevation={8}
			data-component="support-report-panel"
			sx={{
				position: "fixed",
				right: 24,
				bottom: 96,
				// Fixed, not content-sized: a panel that grows and shrinks as
				// you move between conversations moves its own controls under
				// the pointer, which reads as the app twitching.
				width: 560,
				height: 620,
				// …but never taller than the window it is anchored inside. A
				// card of 620 above `bottom: 96` needs 716px, and a window
				// with less than that pushed its own top row — the tabs and
				// the close button — off the screen.
				maxHeight: "calc(100dvh - 112px)",
				display: "flex",
				flexDirection: "column",
				zIndex: theme.zIndex.speedDial,
				[fullScreen]: {
					// Both edges, and no height at all. `100vh` on iOS is the
					// toolbars-hidden viewport while a fixed element sits in
					// the current one, so the card overflowed upward by the
					// difference and took the tab row with it; the on-screen
					// keyboard, which shrinks the layout viewport, made it
					// worse. Anchoring follows the viewport that is actually
					// there, keyboard and all.
					inset: 0,
					width: "auto",
					height: "auto",
					maxHeight: "none",
					// Nothing to round against: the panel IS the screen.
					borderRadius: 0,
				},
			}}
		>
			{/* No title bar: it said nothing the tabs do not, and a fixed panel
			    has no height to spend on a heading. Close sits at the end of
			    the tab row instead, which costs no vertical space at all. */}
			<Stack direction="row" alignItems="center" sx={{ minWidth: 0 }}>
				<Box sx={{ flex: 1, minWidth: 0 }}>{tabs}</Box>
				<IconButton
					size="small"
					onClick={onClose}
					sx={{ mx: 0.5 }}
					data-component="support-report-close"
				>
					<CloseIcon fontSize="small" />
				</IconButton>
			</Stack>

			{conversation ?? (
				<Stack
					spacing={2}
					sx={{
						p: 2,
						flex: 1,
						minHeight: 0,
						overflowY: "auto",
						// The end of this box is the end of the gesture — a
						// drag past it used to carry on into the page behind.
						overscrollBehavior: "contain",
					}}
				>
					{/* The same composer as a reply, because it is the same act.
					    It was a labelled field with a tile strip under it and Send
					    in its own bar — a second widget to learn, and the one
					    place Ctrl+V did nothing. */}
					<SupportComposer
						layout="stacked"
						minRows={3}
						compact
						autoFocus
						value={message}
						onChange={onMessageChange}
						placeholder="Tell us what you need — a problem, a question, or an idea."
						onSend={onSend}
						isSending={isSending}
						pictures={pictures}
						inputRef={inputRef}
						onAddPicture={onUploadPicture}
						onEditPicture={onEditPicture}
						onRemovePicture={onRemovePicture}
						sources={{
							isCapturing,
							onAddPicture,
							onAddOtherSurface,
						}}
						dataComponent="support-report"
					/>

					{error && (
						<Alert severity="error" data-component="support-report-error">
							{error}
						</Alert>
					)}
				</Stack>
			)}
		</Paper>
	);
};

export default SupportReportPanel;
